<template>
  <div class="asc-maintenance-list-template">
    <asc-container class="asc-maintenance-container">
      <template slot="header">
        <asc-grid-wrapper-header :title="title" :tooltip="tooltip" />
      </template>
      <template slot="subheader">
        See Ascentia's cross platform, hardware agnostic, nose-to-tail
        analytics. For assistance with activating analytics, contact our support
        team at <a href="mailto: ascentia@collins.com">ASCENTIA@collins.com</a>.
      </template>
      <asc-ag-grid-filter
        :key="`asc-ascentia-analytics-grid_${gridId}`"
        :on-open="onOpen"
        :grid-options="agGridOptions"
        :column-defs="getGridColumnDefs"
        :row-data="agGridRowData"
        :get-row-height="getRowHeight"
        grid-size="max"
        class="mt-4"
      />
    </asc-container>
  </div>
</template>

<script>
import {
  AscAgGridAutocompleteFloatFilter,
  AscAgGridFilter,
  AscAnalyticsAlertTypesFilter,
  AscAnalyticsAlertTypesFloatFilter,
  AscLinkCellRenderer,
} from '@/components/agGrid'
import { stringTextMatcher } from '@/components/agGrid/AgGridHelperMethods'
import { getFilterOptions } from '@/components/agGrid/AgGridOptions'
import { AscGridMethodsMixin, AscGridPropsMixin } from '@/components/mixins'
import { COLLINS_PHM_TIER_3 } from '@/utilities/appConsts'
import AscContainer from '../atoms/AscContainer.vue'
import AscGridWrapperHeader from '../molecules/AscGridWrapperHeader.vue'

export default {
  name: 'AscAscentiaAnalyticsListTemplate',

  components: {
    AscContainer,
    AscGridWrapperHeader,
    AscAgGridFilter,
    AscLinkCellRenderer,
    AscAgGridAutocompleteFloatFilter,
    AscAnalyticsAlertTypesFilter,
    AscAnalyticsAlertTypesFloatFilter,
  },

  mixins: [AscGridPropsMixin, AscGridMethodsMixin],

  props: {
    userAirlines: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      agGridOptions: getFilterOptions(this, {
        onGridReady: this.handleGridReady,
        onFirstDataRendered: this.handleFirstDataRendered,
        onGridSizeChanged: this.handleGridSizeChanged,
        onCellClicked: this.handleCellClicked,
      }),
    }
  },

  computed: {
    mappedValues() {
      return this.values.map(item => {
        return {
          ...item,
          status: item.isActive ? 'Activated' : 'Inactive',
          overRideDefaultMxRecDetails: this.userAirlines
            .find(airline => airline.airlineId === item.airlineId)
            ?.phmAgreementTypes.includes(COLLINS_PHM_TIER_3),
        }
      })
    },
    agGridRowData() {
      return this.mappedValues
    },
    getGridColumnDefs() {
      return [
        this.createColumnDef('name', {
          sort: 'asc',
          cellRenderer: 'AscLinkCellRenderer',
          cellRendererParams: {
            callback: params => this.handleAnalyticsDetailView(params),
          },
        }),
        this.createColumnDef('airlineName', {
          width: 100,
          hide: this.userAirlines.length === 1,
        }),
        this.createColumnDef('status', {
          cellRenderer: this.statusRenderer,
          width: 100,
        }),
        this.createColumnDef('alertTypes', {
          cellRenderer: this.alertTypesRenderer,
          minWidth: 500,
          filter: 'AscAnalyticsAlertTypesFilter',
          floatingFilterComponent: 'AscAnalyticsAlertTypesFloatFilter',
        }),
        this.createColumnDef('platform', {
          width: 80,
        }),
        this.createColumnDef('ataChapter', { width: 80 }),
      ]
    },
  },

  watch: {
    values() {
      this.handleRefreshColWidths()
    },
  },

  methods: {
    createColumnDef(field, options = {}) {
      const columnDef = {
        headerName: this.getHeaderName(field),
        field: field,
        filter: 'agTextColumnFilter',
        filterParams: {
          textMatcher: stringTextMatcher,
        },
        flex: 1,
        floatingFilterComponent: 'AscAgGridAutocompleteFloatFilter',
        floatingFilterComponentParams: {
          ids: [],
          items: this.getUniqueItems(field),
          suppressFilterButton: true,
        },
        ...options,
      }
      return columnDef
    },

    handleAnalyticsDetailView(params) {
      this.$emit('onAnalyticDetailsView', params)
    },

    getHeaderName(field) {
      switch (field) {
        case 'name':
          return 'analytic Category'
        case 'airlineName':
          return 'Airline'
        case 'status':
          return 'status'
        case 'alertTypes':
          return 'alerts'
        case 'platform':
          return 'Platform'
        case 'ataChapter':
          return 'Ata Chapters'
        default:
          return field
      }
    },

    airlineRenderer(params) {
      return params.data.airlineName
    },

    statusRenderer(params) {
      const status = params.data.isActive

      return `<i class='mdi ${
        status ? 'mdi-check' : 'mdi-close'
      }' style='color:#50D264; font-size: 20px'></i> ${params.value}`
    },

    alertTypesRenderer(params) {
      const alertTypes = params.value
      const rowHeight = this.getRowHeight(params)
      let html = `<div class="v-data-table v-data-table--fixed-height theme--light ag-grid-column-table">
                    <div class="v-data-table__wrapper" style="max-height: ${
                      rowHeight >= 175 ? 175 : rowHeight
                    }px;">
                        <table>
                            <tbody>`
      alertTypes.forEach(alert => {
        html += `<tr>
                    <td style="width: 30%; white-space: nowrap; text-overflow:ellipsis; overflow: hidden; max-width:1px;">${
                      alert.analyticName
                    } </td>
                    <td style="width: 30%; white-space: nowrap; text-overflow:ellipsis; overflow: hidden; max-width:1px;">${
                      alert.name
                    } </td>
                    <td style="width: 20%; white-space: nowrap; text-overflow:ellipsis; overflow: hidden; max-width:1px;">${
                      alert.component
                    }(${alert.position})</td>
                    <td style="width: 20%; white-space: nowrap; text-overflow:ellipsis; overflow: hidden; max-width:1px;">${
                      alert.isActive
                        ? "<i class='mdi mdi-check' style='color:#50D264; font-size: 20px'></i> Activated"
                        : "<i class='mdi mdi-close' style='color:#D20532; font-size: 20px'></i> Inactive"
                    }</td>
                </tr>`
      })
      html += `</tbody>
                    </table>
                        </div>
                            </div>`
      return html
    },

    getRowHeight(params) {
      const height = 36 // adjust this value as per need
      if (params.node && params.node.group) {
        return height
      }

      const data = params.data
      if (!data.alertTypes) return undefined // default height if no alert types

      const numRows = data.alertTypes.length
      const calculatedRowHeight = numRows * height + 30 // adjust this value as per need
      return calculatedRowHeight >= 175 ? 175 + 30 : calculatedRowHeight
    },
  },
}
</script>

<style lang="scss">
.ag-grid-column-table {
  &.theme--light {
    border: 1px solid #d1d1d1;
    border-radius: 0;
  }

  & > .v-data-table__wrapper > table > tbody > tr > td {
    height: 36px;
    padding: 8px 10px 8px 10px;
  }
}
</style>
